/**
 * BlogTemplatesPage — template library at /content/templates.
 *
 * Phase 1 decomposition: this component only COMPOSES hooks and extracted
 * presentational components. All state lives in hooks (useTemplateFilters,
 * useTemplateSelection, useAutoTemplateDialog, useDeleteTemplateDialog); all
 * visual anatomy lives in components (TemplateGallery, TemplateCard,
 * RecipeDetailPane, EditFocusSurface, dialogs) — so the Phase 3 restyle is
 * presentation-only and touches no hook/state code (adjudication A5.3).
 *
 * Adjudicated Phase 1 changes vs the pre-decomposition page:
 * - Standard/Yours grouping replaces the All/System/My tabs (R2).
 * - Bookmark UI ("Save to My Templates") removed (R2; tag endpoints and the
 *   useTaggedTemplates hook remain for the evidence-gated Pinned fallback).
 * - One editor: the 500-line hand-built inline config editor is replaced by
 *   AutoTemplateConfigForm's machinery (data shape + serializer pair);
 *   the library detail view is the S4 RecipeDetailPane ("What you'll get"),
 *   and committed editing happens in the S5 EditFocusSurface.
 * - Standard (system) templates show no Edit affordance (Fable §2.3).
 * - ?template= deep link: read-preselect (Phase 0) + write-back on selection
 *   (acceptance item, in useTemplateSelection).
 * - All config reads/writes go through the recipe serializer pair
 *   (defaultConfigToRecipe / recipeToDefaultConfig) — no per-field
 *   topConfig.x || config.x fallbacks remain.
 */

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import loadingAnimation from '@/assets/loading-animation.webp';
import { useToast } from '@/hooks/use-toast';
import { Zap } from 'lucide-react';
import { blogTemplatesService } from '@/services/blogTemplatesService';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { useBlogTemplates } from '@/hooks/useBlogTemplates';
import { useTemplateFilters } from '@/hooks/useTemplateFilters';
import { useTemplateSelection } from '@/hooks/useTemplateSelection';
import { useAutoTemplateDialog } from '@/hooks/useAutoTemplateDialog';
import { useDeleteTemplateDialog } from '@/hooks/useDeleteTemplateDialog';
import { TemplateGallery } from '@/components/blog-templates/TemplateGallery';
import { TemplatePreviewDrawer } from '@/components/blog-templates/TemplatePreviewDrawer';
import { RecipeDetailPane } from '@/components/blog-templates/RecipeDetailPane';
import { AutoTemplateDialog } from '@/components/blog-templates/AutoTemplateDialog';
import { DeleteTemplateDialog } from '@/components/blog-templates/DeleteTemplateDialog';
import { type AutoTemplateFormData } from '@/components/blog-templates/AutoTemplateConfigForm';
import { EditFocusSurface } from '@/components/blog-templates/EditFocusSurface';
import { parseShapeFloor } from '@/components/blog-templates/shape/shapeFloor';
import {
  defaultConfigToRecipe,
  formDataToRecipe,
  recipeToDefaultConfig,
} from '@/components/blog-templates/recipePayload';

const BlogTemplatesPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Data
  const { data: templatesData, isLoading, error: queryError, refetch } = useBlogTemplates();
  const templates = useMemo(() => templatesData?.templates || [], [templatesData]);
  const error = queryError ? 'Failed to load templates. Please try again.' : null;

  // Hooks own all state
  const {
    searchQuery,
    setSearchQuery,
    yoursTemplates,
    standardTemplates,
    clearFilters,
  } = useTemplateFilters(templates);

  const {
    selectedPreviewTemplate,
    fullTemplateData,
    isLoadingPreview,
    handleSelectTemplate,
    clearSelection,
    handleUseTemplate,
    setFullTemplateData,
    retryFetch,
  } = useTemplateSelection(templates);

  const {
    showAutoTemplateDialog,
    setShowAutoTemplateDialog,
    isCreatingAutoTemplate,
    autoTemplateInitialData,
    handleOpenCreateDialog,
    handleCustomizeTemplate,
    handleCreateAutoTemplate,
  } = useAutoTemplateDialog();

  const {
    deleteConfirmTemplate,
    handleOpenDeleteConfirm,
    handleCancelDelete,
    handleDeleteTemplate,
    isDeleting,
  } = useDeleteTemplateDialog({
    selectedTemplateId: selectedPreviewTemplate?.id ?? null,
    onClearSelection: clearSelection,
  });

  // One-editor edit mode (local to the detail pane; the form owns its data)
  const [isEditingAutoConfig, setIsEditingAutoConfig] = useState(false);
  // Preserve staging's rendered layout preview alongside the S4 detail pane.
  const [layoutPreviewOpen, setLayoutPreviewOpen] = useState(false);
  // W7/W8 + boundary ruling: the drawer is NEVER part of the selection
  // step. It opens only from the "Expand shape" slots (pane button / card
  // overflow) and tracks its own template, decoupled from selection.
  const [drawerTemplate, setDrawerTemplate] = useState<BlogTemplateSummary | null>(null);
  const handleExpandShape = (template: BlogTemplateSummary) => {
    setDrawerTemplate(template);
    setLayoutPreviewOpen(true);
  };
  const handleDrawerOpenChange = (open: boolean) => {
    setLayoutPreviewOpen(open);
    if (!open) setDrawerTemplate(null);
  };
  const [isSavingAutoConfig, setIsSavingAutoConfig] = useState(false);

  // Single deserialization seam — view mode and edit initial data both derive
  // from the canonical recipe, never from raw default_config.
  const recipe = useMemo(
    () => defaultConfigToRecipe(fullTemplateData?.default_config),
    [fullTemplateData],
  );

  const handleSaveAutoConfig = async (config: AutoTemplateFormData) => {
    if (!selectedPreviewTemplate || !fullTemplateData) return;

    setIsSavingAutoConfig(true);
    try {
      await blogTemplatesService.update(selectedPreviewTemplate.id, {
        // `existing` preserves unmanaged keys (e.g. seo_targets) on save.
        default_config: recipeToDefaultConfig(
          formDataToRecipe(config),
          fullTemplateData.default_config,
        ),
      });

      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', selectedPreviewTemplate.id] });

      const updatedTemplate = await blogTemplatesService.get(selectedPreviewTemplate.id);
      setFullTemplateData(updatedTemplate);

      toast({
        title: 'Configuration saved',
        description: 'Auto template settings have been updated',
      });
      setIsEditingAutoConfig(false);
    } catch (err) {
      console.error('Failed to save auto config:', err);
      toast({
        title: 'Save failed',
        description: 'Failed to update template configuration',
        variant: 'destructive',
      });
    } finally {
      setIsSavingAutoConfig(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Choose a Template</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleOpenCreateDialog} className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Create Auto Template
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground">
          Select a template to preview, then click "Use Template" to continue
        </p>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div className="flex-1 flex gap-6 min-h-0 overflow-hidden">
        {/* Left Panel - Template Gallery (grid or split list) */}
        <div
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
        >
          <TemplateGallery
            yoursTemplates={yoursTemplates}
            standardTemplates={standardTemplates}
            isLoading={isLoading}
            error={error}
            onRetry={() => refetch()}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onClearFilters={clearFilters}
            splitMode={false}
            selectedTemplateId={selectedPreviewTemplate?.id ?? null}
            onSelect={(template) => {
              handleSelectTemplate(template);
            }}
            onUse={handleUseTemplate}
            onExpandShape={handleExpandShape}
            onCustomize={handleCustomizeTemplate}
            onDelete={handleOpenDeleteConfirm}
            onCreateTemplate={handleOpenCreateDialog}
          />
        </div>

        {/* S4: "What you'll get" OVERLAY detail pane — the grid stays a
            grid behind it (no list collapse, no scroll loss). View mode
            derives from the summary's own default_config via the canonical
            deserializer; committed editing mounts the S5 EditFocusSurface
            through this same editContent branch. */}
        {selectedPreviewTemplate && (
          <RecipeDetailPane
            template={selectedPreviewTemplate}
            onClose={clearSelection}
            onUse={handleUseTemplate}
            onExpandShape={handleExpandShape}
            onCustomize={handleCustomizeTemplate}
            onDelete={handleOpenDeleteConfirm}
            canDelete={blogTemplatesService.canDelete(selectedPreviewTemplate)}
            isEditing={isEditingAutoConfig && !selectedPreviewTemplate.is_system}
            onToggleEdit={
              !selectedPreviewTemplate.is_system
                ? () => setIsEditingAutoConfig((v) => !v)
                : undefined
            }
            editContent={
              isLoadingPreview ? (
                <div className="h-full flex items-center justify-center p-8">
                  <img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
                </div>
              ) : fullTemplateData?.default_config ? (
                <EditFocusSurface
                  template={selectedPreviewTemplate}
                  initialRecipe={recipe}
                  floor={parseShapeFloor(selectedPreviewTemplate.shape_floor)}
                  onSave={handleSaveAutoConfig}
                  onCancel={() => setIsEditingAutoConfig(false)}
                  isSaving={isSavingAutoConfig}
                />
              ) : (
                // Distinct from a loading state: the detail fetch failed or
                // the template carries no configuration.
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                  <p className="text-lg font-medium mb-1">Configuration unavailable</p>
                  <p className="text-muted-foreground mb-4 max-w-md">
                    This template's settings could not be loaded. You can still use it,
                    or try selecting it again.
                  </p>
                  <Button variant="outline" onClick={retryFetch}>
                    Retry
                  </Button>
                </div>
              )
            }
          />
        )}
      </div>

      <TemplatePreviewDrawer
        template={drawerTemplate}
        open={layoutPreviewOpen && !!drawerTemplate}
        onOpenChange={handleDrawerOpenChange}
        onUse={(template) => {
          setLayoutPreviewOpen(false);
          setDrawerTemplate(null);
          handleUseTemplate(template);
        }}
      />

      {/* Delete Confirmation */}
      <DeleteTemplateDialog
        template={deleteConfirmTemplate}
        isDeleting={isDeleting}
        onConfirm={handleDeleteTemplate}
        onOpenChange={(open) => {
          if (!open) handleCancelDelete();
        }}
      />

      {/* Auto Template Creation/Copy Dialog */}
      <AutoTemplateDialog
        open={showAutoTemplateDialog}
        isCreating={isCreatingAutoTemplate}
        initialData={autoTemplateInitialData}
        onSave={handleCreateAutoTemplate}
        onOpenChange={setShowAutoTemplateDialog}
      />
    </div>
  );
};

export default BlogTemplatesPage;
